/**
 * ChatMarkdown — markdown renderer for AI-authored content outside the chat
 * transcript (currently the scenario example responses).
 *
 * Mirrors ChatDisasterGPT's message pipeline so example responses render
 * exactly like real agent output: GFM (tables), a sanitize-by-default pipeline
 * (rehypeSanitize stays even for server-authored content — it's the XSS guard),
 * the custom-markdown table classes, and links that always open in a new tab.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

const markdownComponents = {
  table({ ...props }) {
    return <table className="custom-markdown-table" {...props} />;
  },
  th({ ...props }) {
    return <th className="custom-markdown-th" {...props} />;
  },
  td({ ...props }) {
    return <td className="custom-markdown-td" {...props} />;
  },
  tr({ ...props }) {
    return <tr className="custom-markdown-tr" {...props} />;
  },
  a({ ...props }) {
    return <a {...props} target="_blank" rel="noopener noreferrer" />;
  },
};

const customSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    span: [...(defaultSchema.attributes?.span || []), 'style'],
  },
};

type ChatMarkdownProps = {
  children: string;
};

const ChatMarkdown: React.FC<ChatMarkdownProps> = ({ children }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    rehypePlugins={[
      [rehypeRaw, { passThrough: ['element'] }],
      [rehypeSanitize, customSchema],
    ]}
    components={markdownComponents}
  >
    {children}
  </ReactMarkdown>
);

export default ChatMarkdown;
